import Controller, { OnAfterNavigate, OnBeforeSubmit, OnChange, OnClick, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, AutocompleteAPI, DateFieldAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, RadioGroupAPI, SelectableItem, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent, MountEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, ModelService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import BaseController from '../../../BaseController';
import { Value } from '@philips-internal/oswald-ui-core';
import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, HealthHistorySocialHistoryEditAliases } from '../../../../codegen/index';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import DialogService from '../../../../services/dialog-service';
import { DomainType, HealthHistorySocialHistoryResponse, HealthHistoryType, SearchType } from '../../health-history-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { ApplicationRoute } from '../../../../constants/routes';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { YesNoType } from '../../../../utils/enum-utils';
import { WizardSteps } from '../../../../constants/wizards';

interface ClassificationDomain {
  description: string;
  id: string;
  valueDomain: string;
}

@Controller({ component: HealthHistorySocialHistoryEditAliases.HEALTH_HISTORY_SOCIAL_HISTORY_ADD_LAYOUT, fragment: FragmentNames.HEALTH_HISTORY_SOCIAL_HISTORY_EDIT })
export default class HealthHistorySocialHistortEditController extends BaseController {

  @Inject(HealthHistorySocialHistoryEditAliases.FORM_ONE)
  private formOneSocialHistoryEditAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FORM_TWO)
  private formTwoSocialHistoryEditAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FORM_THREE)
  private formThreeSocialHistoryEditAPI!: FormAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.WIZARD)
  private wizardAPI!: WizardAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.AUTOCOMPLETE_CLASSIFICATION_DOMAIN)
  private classificationAutoCompleteAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.AUTOCOMPLETE_TYPE_SEARCH)
  private typeSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FETCH_TYPE_SEARCH)
  private typeFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.AUTOCOMPLETE_BRAND_SEARCH)
  private brandSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FETCH_BRAND_SEARCH)
  private brandFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.AUTOCOMPLETE_UNIT_MEASURE_SEARCH)
  private unitMeasureSearchAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FETCH_UNIT_MEASURE_SEARCH)
  private unitMeasureFetchSearchAPI!: FetchAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.INPUT_QUANTITY)
  private inputQuantityApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.INPUT_DESCRIPTIVE_QUANTITY)
  private inputDescriptiveQuantityApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.AUTOCOMPLETE_FREQUENCY_DOMAIN)
  private frequencyAutoCompleteAPI!: AutocompleteAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.INPUT_AGE_EXPERIENCE)
  private inputAgeExperienceApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.INPUT_AGE_REGULAR_USE)
  private inputAgeRegularUseApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.INPUT_TOTAL_PACKS)
  private inputTotalPacksApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.DATE_FIELD_START_DATE)
  private startDateInputApi!: DateFieldAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FIELD_START_DATE)
  private fieldStartDateInputApi!: FieldAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.DATE_FIELD_END_DATE)
  private endDateInputApi!: DateFieldAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.FIELD_END_DATE)
  private fieldEndDateInputApi!: FieldAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.TEXT_AREA_CONSEQUENCES)
  private textAreaConsequencesApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.TEXT_AREA_FAMILY_ANTECEDENTS)
  private textAreaFamilyAntecedentsApi!: InputAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.ACTION_DELETE_HEALTH_HISTORY_SOCIAL_HISTORY)
  private actionDeleteAPI!: ActionAPI;

  @Inject(HealthHistorySocialHistoryEditAliases.ACTION_RELEASE_HEALTH_HISTORY_SOCIAL_HISTORY)
  private actionReleaseAPI!: ActionAPI;

  @OnValidate(HealthHistorySocialHistoryEditAliases.FORM_ONE)
  handleOnValidate(validation: ValidateEvent): void {
    this.setStepDone(validation.valid, WizardSteps.STEP_ONE)
  }

  @OnValidate(HealthHistorySocialHistoryEditAliases.FORM_TWO)
  handleOnSecondValidate(validationFormTwo: ValidateEvent): void {
    this.setStepDone(validationFormTwo.valid, WizardSteps.STEP_TWO)
  }

  @OnValidate(HealthHistorySocialHistoryEditAliases.FORM_THREE)
  handleOnTirdValidate(validationFormThree: ValidateEvent): void {
    this.setStepDone(validationFormThree.valid, WizardSteps.STEP_THREE)
  }

  @OnMount(HealthHistorySocialHistoryEditAliases.RADIO_GROUP_CONDITION)
  handleMountRadioCondition(event: MountEvent): void {
    const radioGroupAPI = event.target as RadioGroupAPI;
    const conditionExpression = 337373;
    if (radioGroupAPI) {
      this.scaleRadioGroup(conditionExpression, radioGroupAPI)
    }
  }

  @OnMount({ target: HealthHistorySocialHistoryEditAliases.FORM_ONE })
  handleMountForm(): void {
    applicationContext.popNavigationItem();
    this.formOneSocialHistoryEditAPI.setLoading(true);
    this.actionDeleteAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
  }

  @OnMount({ target: HealthHistorySocialHistoryEditAliases.WIZARD })
  handleFormMount(): void {
    HealthHistoryListContext.currentTab = HealthHistoryType.SOCIAL_HISTORY
    const { encounterId, patientId, idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.loadEditionData(idHealthHistory as number);
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  @OnAfterNavigate(HealthHistorySocialHistoryEditAliases.WIZARD)
  handleOnMountWizardStepTwo(): void {
    this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
    this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
  }

  private setStepDone(conditionToSetStepDone: boolean, step: WizardSteps): void {
    if (conditionToSetStepDone) {
      this.wizardAPI.setStepDone(step, true);
    }
  }

  private deniesHabits(): void {
    this.classificationAutoCompleteAPI.setValue(undefined);
    this.typeSearchAPI.setValue(undefined);
    this.brandSearchAPI.setValue(undefined);
    this.unitMeasureSearchAPI.setValue(undefined);
    this.inputQuantityApi.setValue('');
    this.inputDescriptiveQuantityApi.setValue('');
    this.frequencyAutoCompleteAPI.setValue(undefined);
    this.inputAgeExperienceApi.setValue('');
    this.inputAgeRegularUseApi.setValue('');
    this.inputTotalPacksApi.setValue('');
    this.startDateInputApi.setValue('');
    this.endDateInputApi.setValue('');
    this.textAreaConsequencesApi.setValue('');
    this.textAreaFamilyAntecedentsApi.setValue('');
    this.fieldStartDateInputApi.setValue('');
    this.fieldEndDateInputApi.setValue('');
  }

  private toggleEnabled(isEnabled: boolean): void {
    this.classificationAutoCompleteAPI.setEnabled(isEnabled);
    this.inputQuantityApi.setEnabled(isEnabled);
    this.inputDescriptiveQuantityApi.setEnabled(isEnabled);
    this.frequencyAutoCompleteAPI.setEnabled(isEnabled);
    this.inputAgeExperienceApi.setEnabled(isEnabled);
    this.inputAgeRegularUseApi.setEnabled(isEnabled);
    this.inputTotalPacksApi.setEnabled(isEnabled);
    this.startDateInputApi.setEnabled(isEnabled);
    this.endDateInputApi.setEnabled(isEnabled);
    this.textAreaConsequencesApi.setEnabled(isEnabled);
    this.textAreaFamilyAntecedentsApi.setEnabled(isEnabled);
    this.frequencyAutoCompleteAPI.setEnabled(isEnabled);
  }

  @OnChange(HealthHistorySocialHistoryEditAliases.CHECKBOX_DENIES_HABITS)
  handleDeniesHabits(event: ChangeEvent<YesNoType.YES>): void {
    const value = event.value;
    if (value) {
      this.deniesHabits();
      this.toggleEnabled(false);
    } else {
      this.toggleEnabled(true);
    }
  }

  private isNotEmpty(object?: SearchType): boolean {
    return !!object && Object.keys(object).length !== 0;
  }

  private scaleRadioGroup(expressionId: number, radioGroup: RadioGroupAPI): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);
        const socialHistory = this.context.get('socialHistory') as HealthHistorySocialHistoryResponse;
        const condition = responseBody.find(({ key }) => {
          return String(socialHistory?.condition) === (key);
        })
        if (condition) {
          radioGroup.setValue(condition)
        }
        this.context.set({
          socialHistory: {
            ...socialHistory,
            condition
          }
        })
        this.formThreeSocialHistoryEditAPI.setData({
          ...this.formThreeSocialHistoryEditAPI.getData(),
          condition
        })
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR);
      });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-social-history',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private showModalSubmit(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.historyService.back(); },
      onClickOk: () => { this.releaseSocialHistory(idHealthHistory); },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
  }

  private releaseSocialHistory(idHealthHistorySocialHistory: number): void {
    this.formOneSocialHistoryEditAPI.setLoading(true);
    HttpService.put(Endpoint.RELEASE_HEALTH_HISTORY_SOCIAL_HISTORY, { sequenceNumberSocialHistory: idHealthHistorySocialHistory })
      .then(() => {
        this.formOneSocialHistoryEditAPI.setLoading(false);
        this.goBack();
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
        this.formOneSocialHistoryEditAPI.setLoading(false)
      });
  }

  private createNewSocialHistory(body: Record<string, Value>): void {
    this.formOneSocialHistoryEditAPI.setLoading(true);
    HttpService.post(Endpoint.POST_HEALTH_HISTORY_SOCIAL_HISTORY, undefined, { body })
      .then(response => {
        const { idHealthHistory } = response.body as HealthHistorySocialHistoryResponse;
        this.showModalSubmit(idHealthHistory);
        this.context.set({ idHealthHistory, isCreated: true });
      })
      .catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR);
      }).finally(() => this.formOneSocialHistoryEditAPI.setLoading(false));
  }

  private loadEditionData(idHealthHistory: number): void {
    const sequenceNumberSocialHistory = idHealthHistory;
    HttpService.get(Endpoint.HEALTH_HISTORY_SOCIAL_HISTORY, { sequenceNumberSocialHistory })
      .then(response => {
        const record = response.body as HealthHistorySocialHistoryResponse;
        const socialHistory = { ...record, condition: { key: record.condition, label: '', value: record.condition } }
        this.formOneSocialHistoryEditAPI.setEditing(true);
        this.formOneSocialHistoryEditAPI.validate();
        this.formOneSocialHistoryEditAPI.setDisabled(true);
        this.context.set({ socialHistory: record });

        this.typeSearchAPI.setEnabled(this.isNotEmpty(record.type));
        this.brandSearchAPI.setEnabled(this.isNotEmpty(record.brand));
        this.unitMeasureSearchAPI.setEnabled(this.isNotEmpty(record.unitMeasure));

        this.formOneSocialHistoryEditAPI.setData(record as unknown as Record<string, HealthHistorySocialHistoryResponse>);
        this.formTwoSocialHistoryEditAPI.setData(record as unknown as Record<string, HealthHistorySocialHistoryResponse>);
        this.formThreeSocialHistoryEditAPI.setData(socialHistory as unknown as Record<string, HealthHistorySocialHistoryResponse>);
        this.treatData(record);

        this.actionDeleteAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);

        this.wizardAPI.setStepDone(WizardSteps.STEP_ONE, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_TWO, true);
        this.wizardAPI.setStepDone(WizardSteps.STEP_THREE, true);
      }).catch((error) => {
        error?.body?.errorMessage && this.showToast(error.body.errorMessage, ToastLevel.ERROR)
      }).finally(() => {
        this.formOneSocialHistoryEditAPI.setLoading(false);
      });
  }

  @OnBeforeSubmit({ target: HealthHistorySocialHistoryEditAliases.WIZARD })
  handleBeforeSubmit(): void {
    const { encounterId, patientId, idHealthHistory } = this.historyService.getCurrentRouteParams();
    const isCreated = this.context.get('isCreated');
    if (isCreated) {
      this.showModalSubmit(idHealthHistory as number);
      return;
    }
    const formDataOne = this.formOneSocialHistoryEditAPI.getRecord();
    const formDataTwo = this.formTwoSocialHistoryEditAPI.getRecord();
    const formDataThree = this.formThreeSocialHistoryEditAPI.getRecord();
    const deniesHabits = BooleanUtils.isPositiveValue(formDataOne.deniesHabits as boolean);
    const alert = BooleanUtils.isPositiveValue(formDataOne.alert as boolean);
    const classification = (formDataOne.classification as DomainType)?.valueDomain;
    const type = formDataOne.type as SearchType;
    const brand = formDataOne.brand as SearchType;
    const unitMeasure = formDataOne.unitMeasure as SearchType;
    const amount = formDataTwo.amount || '';
    const descriptiveQuantity = formDataTwo.descriptiveQuantity || '';
    const frequency = (formDataTwo.frequency as DomainType)?.valueDomain;
    const securityLevel = formDataTwo.securityLevel as SearchType;
    const ageExperience = formDataTwo.ageExperience || '';
    const ageRegularUse = formDataTwo.ageRegularUse || '';
    const totalPacks = formDataTwo.totalPacks || '';
    const startDate = ModelService.format('timestamp', formDataThree.startDate);
    const endDate = ModelService.format('timestamp', formDataThree.endDate);
    const problemList = BooleanUtils.isPositiveValue(formDataThree.problemList as boolean);
    const condition = formDataThree.condition as SelectableItem;
    const firstQuarter = BooleanUtils.isPositiveValue(formDataThree.firstQuarter as boolean);
    const secondQuarter = BooleanUtils.isPositiveValue(formDataThree.secondQuarter as boolean);
    const thirdQuarter = BooleanUtils.isPositiveValue(formDataThree.thirdQuarter as boolean);
    const consequences = formDataThree.consequences || '';
    const antecedents = formDataThree.antecedents || '';
    const notes = formDataThree.notes || '';
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const socialHistory = this.context.get('socialHistory') as HealthHistorySocialHistoryResponse;
    const body = {
      ageExperience,
      ageRegularUse,
      alert,
      amount,
      antecedents,
      attendance: encounterId as number,
      brand: brand && brand.id,
      classification,
      condition: condition.key,
      consequences,
      deniesHabits,
      descriptiveQuantity,
      endDate,
      firstQuarter,
      frequency,
      idHealthHistory: idHealthHistory as number,
      notes,
      physicalPerson: patientId,
      problemList,
      professional: user.physicalPersonId,
      recordDate: socialHistory.recordDate,
      secondQuarter,
      securityLevel: securityLevel && securityLevel.id,
      startDate,
      thirdQuarter,
      totalPacks,
      type: type && type.id,
      unitMeasure: unitMeasure && unitMeasure.id,
    } as Record<string, Value>;
    this.createNewSocialHistory(body);
  }

  private treatData(record: HealthHistorySocialHistoryResponse): void {
    const deniesHabits = BooleanUtils.isTrueValue(record.deniesHabits);
    const alert = BooleanUtils.isTrueValue(record.alert);
    const problemList = BooleanUtils.isTrueValue(record.problemList);

    const data = {...record, alert, condition: {key: '', label: '', value: '' }, deniesHabits, problemList }
    if(record.deniesHabits === YesNoType.YES){
      this.deniesHabits();
      this.toggleEnabled(false);
    }
    this.formOneSocialHistoryEditAPI.setData(data);
    this.formTwoSocialHistoryEditAPI.setData(data);
    this.formThreeSocialHistoryEditAPI.setData(data);
  }

  private delete(idHealthHistory: number): void {
    const sequenceNumberSocialHistory = idHealthHistory;
    this.formOneSocialHistoryEditAPI.setLoading(true);
    HttpService.delete(Endpoint.HEALTH_HISTORY_SOCIAL_HISTORY, {
      sequenceNumberSocialHistory
    }).then(() => {
      this.formOneSocialHistoryEditAPI.setLoading(false);
      this.goBack();
    }).catch((err) => {
      this.showToast(err?.body, ToastLevel.ERROR);
      this.formOneSocialHistoryEditAPI.setLoading(false)
    });
  }

  private confirmDelete(idHealthHistory: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.delete(idHealthHistory); },
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  @OnChange(HealthHistorySocialHistoryEditAliases.FIELD_CLASSIFICATION_DOMAIN)
  handleClassificationChange(event: ChangeEvent<ClassificationDomain>): void {
    const value = event.value;
    this.typeSearchAPI.setValue(undefined);
    this.typeSearchAPI.setEnabled(false);
    this.brandSearchAPI.setValue(undefined);
    this.brandSearchAPI.setEnabled(false);
    this.unitMeasureSearchAPI.setValue(undefined);
    this.unitMeasureSearchAPI.setEnabled(false);

    if (value) {
      this.handleTypeField(String(value.valueDomain))
      this.handleUnitMeasureField(String(value.valueDomain))
    }
  }

  private handleTypeField(classificationDomain: string): void {
    this.typeFetchSearchAPI.setParameters({ classificationDomain })
    const params = { classificationDomain }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_TYPES, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.typeSearchAPI.setEnabled(body.length !== 0);
      })
  }

  private handleUnitMeasureField(classificationDomain: string): void {
    this.unitMeasureFetchSearchAPI.setParameters({ classificationDomain })
    const params = { classificationDomain }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_UNIT_MEASURE, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.unitMeasureSearchAPI.setEnabled(body.length !== 0);
      })
  }

  @OnChange(HealthHistorySocialHistoryEditAliases.FIELD_TYPE_SEARCH)
  handleTypeChange(event: ChangeEvent<ClassificationDomain>): void {
    const value = event.value;
    if (value) {
      this.handleBrandField(Number(value.id))
    }
  }

  private handleBrandField(typeId: number): void {
    this.brandFetchSearchAPI.setParameters({ typeId })
    const params = { typeId }
    HttpService.get(Endpoint.GET_HEALTH_HISTORY_SOCIAL_HISTORY_BRAND, params)
      .then(response => {
        const body = response.body as Record<string, Value>[]
        this.brandSearchAPI.setEnabled(body.length !== 0);
      })
  }

  @OnClick({ target: HealthHistorySocialHistoryEditAliases.ACTION_NEW_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_SOCIAL_HISTORY_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: HealthHistorySocialHistoryEditAliases.ACTION_RELEASE_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleClickInactivate(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idHealthHistory as number);
  }

  @OnClick({ target: HealthHistorySocialHistoryEditAliases.ACTION_DELETE_HEALTH_HISTORY_SOCIAL_HISTORY })
  handleClickDelete(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idHealthHistory as number);
  }

  @OnUnmount(HealthHistorySocialHistoryEditAliases.HEALTH_HISTORY_SOCIAL_HISTORY_ADD_LAYOUT)
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.SOCIAL_HISTORY;
  }
}
